<script>
    import { onMount, onDestroy } from 'svelte';
    import {
        claimsStore,
        specStore,
        editorStateStore
    } from './stores/editorsStore.js';
    import { clerkStore } from './stores/clerkStore.js';
    import { termsStore } from './stores/termsStore.js';
    import { navigationStore } from './stores/navigationStore.js';
    import { commentsStore } from './stores/commentsStore.js';
    import { versionStore } from './stores/versionStore.js';
    import { documentMetadataStore } from './stores/documentMetadataStore.js';
    import { initializeManualTermsService } from './services/manualTermsService.js';
    import { PersistentTermsService } from './services/persistentTermsService.js';
        import { exportTextService } from './services/exportTextService.js';
    import { validateWordExport, createValidationReport } from './services/exportValidationService.js';
    import { regenerateDocument } from './services/specRegenerationService.js';
    import { downloadService } from './services/downloadService.js';
    import { storeInitializationService } from './services/storeInitializationService.js';
    import { backendService } from './services/backendService.js';
    import { figureService } from './services/figureService.js';
    import { antibodyTableService } from './services/antibodyTableService.js';
    import { termChildService } from './services/termChildService.js';
    import { termAbbreviationService } from './services/termAbbreviationService.js';
    import { userProfileService } from './services/userProfileService.js';
    import { spellcheckService } from './services/spellcheckService.js';
    import { figureOrderService } from './services/figureOrderService.js';
    import { documentTitleSyncService } from './services/documentTitleSyncService.js';
    import SearchHighlightStyles from './styles/search-highlight-styles.svelte';
    import TermAddingModeStyles from './styles/term-adding-mode-styles.svelte';
    import CommentAnchorStyles from './styles/comments/comment-anchor-styles.svelte';
    import { themeStore } from './stores/themeStore.js';
    import { settingsStore } from './stores/settingsStore.js';

    // Import components
    import EditorsContainer from './components/editors/EditorsContainer.svelte';
    import Toolbar from './components/toolbar/Toolbar.svelte';
    import CommentsPanel from './components/comments/CommentsPanel.svelte';
    import CommentContextMenu from './components/comments/CommentContextMenu.svelte';
    import DiffPanel from './components/diff/DiffPanel.svelte';
    import RecurringNotification from './components/shared/RecurringNotification.svelte';

    // Initialize editor views
    let specEditor;
    let claimsEditorView;
    
    let initError = null;
    let manualTermsService;
    let persistentTermsService;
    let toolbar;
    let userButtonContainer;
    let showDiffPanel = false;
    
    // Progress bar state
    let progressBarVisible = false;
    let progressBarProgress = 0;
    let progressBarTotalSteps = 0;
    let progressBarCurrentStep = 0;
    let progressBarStepsRemaining = 0;
    let progressBarEstimatedTime = null;
    
    // Subscribe to document metadata to update browser title
    $: if ($documentMetadataStore.title) {
        document.title = $documentMetadataStore.title;
    }

    onMount(async () => {
        try {
            storeInitializationService.initialize();

            
            // Mount Clerk user button if available
            const unsubscribeClerk = clerkStore.subscribe(state => {
                if (state.isSignedIn && userButtonContainer && window.clerk) {
                    window.clerk.mountUserButton(userButtonContainer, {
                        appearance: {
                            elements: {
                                userButtonBox: 'clerk-user-button-container',
                                userButtonTrigger: 'clerk-user-button'
                            }
                        }
                    });
                }
            });
            
            // Clear backend state on page refresh/reload (non-blocking)
            backendService.clearBackendState().catch(err => {
                console.error('Failed to clear backend state:', err);
            });

            // Clear all service caches on page load
            figureService.clearCache();
            antibodyTableService.clearCache();
            termChildService.clearAllCachedChildTerms();
            termAbbreviationService.clearAllCachedAbbreviations();
            userProfileService.clearCache();
            spellcheckService.clearCustomDictionary();
            console.log('[App] All service caches cleared on page load (including spellcheck custom dictionary)');

            // Load user settings from backend (including personalization)
            settingsStore.loadFromBackend().catch(err => {
                console.log('[App] Could not load settings from backend, using local settings:', err);
            });

            // Initialize the persistent terms service first
            persistentTermsService = new PersistentTermsService(termsStore, claimsStore, backendService);
            
            // Initialize the manual terms service with persistent terms service
            manualTermsService = initializeManualTermsService(termsStore, claimsStore, specStore, persistentTermsService);

            // Version store uses in-memory storage only (no initialization needed)

            // Start figure order monitoring
            figureOrderService.startMonitoring();
            
            // Start document title sync monitoring
            documentTitleSyncService.startMonitoring();
            
            // Set up change detection
            const unsubscribeSpec = specStore.subscribe(($spec) => {
                versionStore.checkForChanges($spec.content, $claimsStore.content);
            });
            
            const unsubscribeClaims = claimsStore.subscribe(($claims) => {
                versionStore.checkForChanges($specStore.content, $claims.content);
            });
            
            // Initialize search coordinator once editor is available
            // This will be done when specEditor is bound
            
            // Add global keyboard shortcut handler
            const handleKeyDown = (event) => {
                // Check for Ctrl+S (or Cmd+S on Mac) for save
                if ((event.ctrlKey || event.metaKey) && event.key === 's') {
                    event.preventDefault(); // Prevent browser's default save
                    if (toolbar && toolbar.handleSave) {
                        toolbar.handleSave(true); // Pass true for quick save
                    }
                }
                
                // Check for Ctrl+F (or Cmd+F on Mac)
                if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
                    event.preventDefault(); // Prevent browser's default find
                    if (toolbar && toolbar.focusSearchBar) {
                        toolbar.focusSearchBar();
                    }
                }
                
                // Check for Ctrl+Shift+O (or Cmd+Shift+O on Mac) for navigation pane
                if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'O') {
                    event.preventDefault();
                    navigationStore.toggle();
                }
                
                // Check for Ctrl+Shift+C (or Cmd+Shift+C on Mac) for comments panel
                if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'C') {
                    event.preventDefault();
                    commentsStore.toggle();
                }
                
                // Check for Ctrl+D (or Cmd+D on Mac) for diff panel
                if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
                    event.preventDefault();
                    showDiffPanel = !showDiffPanel;
                }
            };
            
            document.addEventListener('keydown', handleKeyDown);
            
            // Return cleanup function
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                unsubscribeSpec();
                unsubscribeClaims();
                figureOrderService.stopMonitoring();
                documentTitleSyncService.stopMonitoring();
            };
        } catch (error) {
            console.error('Failed to initialize application:', error);
            initError = error;
        }
    });
    
    onDestroy(() => {
        // Clean up the manual terms service
        if (manualTermsService) {
            manualTermsService.destroy();
        }
    });

    // Handle spec regeneration
    async function handleRegenerateSpec(event) {
        try {
            // Extract contexts from dropdown selection
            const contexts = event.detail.contexts;
            console.log('App.svelte: Regenerating with contexts:', contexts);

            // Progress callback
            const handleProgress = (progressData) => {
                if (progressData.type === 'start') {
                    progressBarVisible = true;
                    progressBarProgress = 0;
                    progressBarTotalSteps = progressData.totalSteps;
                    progressBarCurrentStep = 0;
                    progressBarEstimatedTime = progressData.estimatedTime || null;
                } else if (progressData.type === 'progress') {
                    progressBarProgress = progressData.progress || 0;
                    progressBarCurrentStep = progressData.completedSteps || 0;
                    progressBarTotalSteps = progressData.totalSteps || 0;

                    // Calculate steps remaining
                    const stepsRemaining = progressBarTotalSteps - progressBarCurrentStep;
                    progressBarStepsRemaining = Math.max(0, stepsRemaining);

                    progressBarEstimatedTime = progressData.estimatedTime || null;
                } else if (progressData.type === 'complete') {
                    progressBarProgress = 100;
                    progressBarCurrentStep = progressData.totalSteps;
                    progressBarStepsRemaining = 0;
                    // Hide after a delay
                    setTimeout(() => {
                        progressBarVisible = false;
                    }, 1500);
                }
            };

            // Will return true when regeneration is complete
            await regenerateDocument(specEditor, $claimsStore.content, contexts, {
                onProgress: handleProgress
            });
        } catch (error) {
            console.error('Error during regeneration:', error);
            progressBarVisible = false;
        }
    }

    // Handle stopping spec generation
    function handleStopSpecGeneration() {
        const wasStopped = backendService.stopSpecRegeneration();
        if (wasStopped) {
            console.log('Spec regeneration stopped successfully');

            // Remove only skeleton loaders (keep already-generated regenerative blocks)
            if (specEditor && specEditor.state) {
                const doc = specEditor.state.doc;
                let tr = specEditor.state.tr;
                const skeletonsToDelete = [];

                // Find only skeleton loaders
                doc.descendants((node, pos) => {
                    if (node.type.name === 'paragraph' &&
                        node.attrs.blockType === 'skeleton-loader') {
                        skeletonsToDelete.push({
                            pos,
                            size: node.nodeSize
                        });
                    }
                });

                console.log(`Found ${skeletonsToDelete.length} skeleton loaders to delete`);

                // Delete skeleton loaders in reverse order to maintain correct positions
                for (let i = skeletonsToDelete.length - 1; i >= 0; i--) {
                    const skeleton = skeletonsToDelete[i];
                    tr = tr.delete(skeleton.pos, skeleton.pos + skeleton.size);
                }

                if (tr.docChanged) {
                    specEditor.view.dispatch(tr);
                    console.log('Removed all skeleton loaders');
                }
            }

            // Hide the progress bar
            progressBarVisible = false;
            // Reset spec regeneration state
            specStore.update(s => ({ ...s, isRegenerating: false }));
            // Also reset analyzing state in editorStateStore
            editorStateStore.update(s => ({ ...s, isAnalyzing: false }));
        } else {
            console.log('No spec regeneration was running');
        }
    }

    // Import search service
    import { searchService } from './services/searchService.js';
    
    // Handle search through coordinator
    function handleSearch({ detail }) {
        const { query, options, editor } = detail;
        searchService.handleSearch(query, options, editor);
    }
    
    // Handle replace through coordinator
    function handleReplace({ detail }) {
        const { search, replace, section, editor } = detail;
        searchService.handleReplace(search, replace, section, editor);
    }
    
    // Handle replace all through coordinator
    function handleReplaceAll({ detail }) {
        const { search, replace, section, editor } = detail;
        searchService.handleReplaceAll(search, replace, section, editor);
    }
    
    
    // Handle clear search through coordinator
    function handleClearSearch() {
        searchService.clearSearch();
    }
    
    // Handle search navigation through coordinator
    function handleSearchNavigation({ detail }) {
        const { direction, editor } = detail;
        if (direction === 'next') {
            searchService.navigateNext(editor);
        } else if (direction === 'prev') {
            searchService.navigatePrev(editor);
        }
    }

    // Handle exports
    async function handleExportToWord() {
        try {
            const blob = await exportTextService.exportToWord(specEditor.getHTML(), $claimsStore.content);
            
            // Validate the exported Word document
            console.log('Validating exported Word document...');
            const validation = await validateWordExport(blob);
            
            if (!validation.isValid) {
                console.error('Export validation failed:');
                console.error(createValidationReport(validation));
                alert('Export failed: The generated Word document is invalid. Please check the console for details.');
                return;
            } else if (validation.warnings.length > 0) {
                console.warn('Export validation warnings:');
                console.warn(createValidationReport(validation));
            } else {
                console.log('Export validation passed:', validation.details);
            }
            
            // Get the document title for the filename
            let suggestedFilename = 'untitled.docx';
            if ($documentMetadataStore.title && $documentMetadataStore.title !== 'Untitled document') {
                // Replace spaces with hyphens and ensure lowercase
                suggestedFilename = $documentMetadataStore.title.toLowerCase().replace(/\s+/g, '-') + '.docx';
            }
            
            // Use the File System Access API for a native save dialog
            const handle = await window.showSaveFilePicker({
                suggestedName: suggestedFilename,
                types: [{
                    description: 'Word Document',
                    accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] }
                }]
            });
            
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            console.log('Word document exported and saved successfully');
        } catch (error) {
            // User cancelled the save dialog
            if (error.name === 'AbortError') {
                console.log('Export cancelled by user');
                return;
            }
            console.error('Error exporting to Word:', error);
            alert(`Export failed: ${error.message}`);
        }
    }


</script>

<SearchHighlightStyles />
<TermAddingModeStyles />
<CommentAnchorStyles />

<div class="app-container">
    <main class="app">
        <h1 class="visually-hidden">Patdraft</h1>
        <div class="toolbar-wrapper">
            <div class="logo-container">
                <img src={$themeStore === 'dark' ? "/patcraft_logo.svg" : "/patcraft_logo.svg"} alt="Patcraft" class="logo" />
            </div>
            <div class="toolbar-container">
                <Toolbar 
                    bind:this={toolbar}
                    on:regenerateSpec={handleRegenerateSpec}
                    on:search={handleSearch}
                    on:clearSearch={handleClearSearch}
                    on:searchNavigation={handleSearchNavigation}
                    on:replace={handleReplace}
                    on:replaceAll={handleReplaceAll}
                    on:focusEditor={({detail}) => {
                        const { editor } = detail;
                        if (editor === 'spec' && specEditor) {
                            specEditor.commands.focus();
                        } else if (editor === 'claims' && claimsEditorView) {
                            claimsEditorView.commands.focus();
                        }
                    }}
                    on:exportToWord={handleExportToWord}
                    on:toggleDiffPanel={() => {
                        showDiffPanel = !showDiffPanel;
                    }}
                    editorView={specEditor}
                    claimsEditor={claimsEditorView}
                />
            </div>
            <!-- Clerk User Button -->
            {#if $clerkStore.isSignedIn}
                <div class="user-button-container">
                    <div bind:this={userButtonContainer} class="clerk-user-button"></div>
                </div>
            {/if}
        </div>
            <EditorsContainer
                bind:specEditor
                bind:claimsEditor={claimsEditorView}
                bind:progressVisible={progressBarVisible}
                bind:progressStepsRemaining={progressBarStepsRemaining}
                bind:progressTotalSteps={progressBarTotalSteps}
                bind:progressEstimatedTime={progressBarEstimatedTime}
                bind:progressPercent={progressBarProgress}
                on:specEditorReady={() => {
                    // Initialize search service when spec editor is ready
                    if (specEditor) {
                        searchService.initialize(specEditor, claimsEditorView);
                    }
                }}
                on:stop={handleStopSpecGeneration}
            />
        </main>
    </div>
<CommentsPanel />
<CommentContextMenu />
<DiffPanel show={showDiffPanel} on:close={() => showDiffPanel = false} />
<RecurringNotification />

<style>
    /* Visually hidden but accessible to screen readers */
    .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
    
    /* Application-level layout styles */
    :global(html) {
        overflow: visible; /* Allow content to be visible beyond viewport */
    }

    :global(body) {
        min-width: var(--viewport-min-width);
        min-height: var(--viewport-min-height);
        padding: 0;
        margin: 0;
        overflow: visible; /* Ensure body also allows visible overflow */
        background-color: var(--color-bg-main);
    }

    .app-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        min-width: var(--viewport-min-width);
        max-width: var(--viewport-max-width);
        height: 100vh;
        min-height: var(--viewport-min-height);
        max-height: var(--viewport-max-height);
        padding: var(--spacing-6);
        margin: 0 auto;
        overflow: auto; /* Allow both horizontal and vertical scrolling */
        box-sizing: border-box;
    }

    main {
        position: relative;
        display: flex;
        flex: 1;
        flex-direction: column;
        width: 100%;
        min-height: 0;
        background-color: var(--color-bg-main);
    }

    .toolbar-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;
        box-sizing: border-box;
        gap: 1rem;
    }

    .logo-container {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        margin-right: 1rem;
    }

    .toolbar-container {
        display: flex;
        flex: 1;
        justify-content: center;
    }

    .logo {
        width: 3.5rem;
        height: 3.5rem;
        object-fit: contain;
    }

    .user-button-container {
        margin-top: 0.5rem;
    }

    /* Clerk user button styling */
    :global(.clerk-user-button-container) {
        display: flex;
        align-items: center;
    }

    :global(.clerk-user-button) {
        border-radius: 50%;
        transition: transform 0.2s ease;
        cursor: pointer;
    }

    :global(.clerk-user-button:hover) {
        transform: scale(1.05);
    }

    /* Global CodeMirror styles - these need to be global due to CodeMirror's nature */
    :global(.CodeMirror) {
        height: 100% !important;
        font-family: var(--font-family-base);
        font-size: var(--font-size-sm);
        line-height: var(--line-height-relaxed);
    }

    :global(.CodeMirror-scroll) {
        height: 100% !important;
    }

    :global(.CodeMirror-sizer) {
        min-height: 100% !important;
    }

    :global(.CodeMirror pre.CodeMirror-line) {
        padding: 0 1rem;
    }

    :global(.CodeMirror-gutters) {
        background: none !important;
        border-right: none !important;
    }

    :global(.CodeMirror-gutter) {
        background: none !important;
    }

    :global(.CodeMirror-gutter-wrapper) {
        background: none !important;
    }

    :global(.CodeMirror-linenumber) {
        color: var(--color-text-tertiary) !important;
        background: none !important;
    }

    :global(.CodeMirror-activeline-background),
    :global(.CodeMirror-activeline-gutter) {
        background: none !important;
    }

    /* Remove transitions */
    :global(.CodeMirror *) {
        transition: none !important;
    }
</style>